// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import {
    AI_CREATE_AG_UI_CUSTOM_EVENT,
    AI_CREATE_STREAM_NODE,
} from '@storefronts/common-types'
import { useAiCreateAgent } from './AICreateRuntimeProvider'

/**
 * The few run facts the chrome needs that the assistant-ui thread doesn't
 * carry: header status, the run-failure banner, and the preview's applied-edit
 * counter. Everything else renders natively from the runtime.
 */
export type AiCreateRunSignals = {
    runActive: boolean
    hasActivity: boolean
    runFailed: boolean
    runErrorMessage: string | null
    /** Applied-edit notes (copy rewrite, revise agent) — the preview reloads
     * on each one instead of waiting for a stage change. */
    editNoteCount: number
    /** `generateLlmContent` finished on this session's stream. */
    contentReady: boolean
    /** Successful content-node finishes. Each one rewrites the page, so the
     * preview reloads without waiting for a variant-status publish. */
    contentStageCount: number
    /** Caption for the latest content-node finish. */
    contentStageLabel: string | null
    /** `reviseStorefronts` finished — the primary pipeline is done, while
     * background variants may still be running. */
    primaryPipelineFinished: boolean
}

const EMPTY_SIGNALS: AiCreateRunSignals = {
    runActive: false,
    hasActivity: false,
    runFailed: false,
    runErrorMessage: null,
    editNoteCount: 0,
    contentReady: false,
    contentStageCount: 0,
    contentStageLabel: null,
    primaryPipelineFinished: false,
}

const EDIT_NOTE_NODES = new Set<string>([
    AI_CREATE_STREAM_NODE.REWRITE_COPY,
    AI_CREATE_STREAM_NODE.REVISE_STOREFRONTS,
])

const CONTENT_STAGE_LABELS: Record<string, string> = {
    [AI_CREATE_STREAM_NODE.GENERATE_LLM_CONTENT]: 'content written',
    [AI_CREATE_STREAM_NODE.CURATE_MEDIA]: 'images added',
    [AI_CREATE_STREAM_NODE.FINALIZE_CONTENT]: 'page ready',
}

type WireEvent = {
    type?: unknown
    runId?: unknown
    message?: unknown
    name?: unknown
    toolCallId?: unknown
    content?: unknown
    value?: { node?: unknown }
}

type SignalState = AiCreateRunSignals & { runId?: string }

function isOtherRun(prev: SignalState, event: WireEvent): boolean {
    return Boolean(
        prev.runId &&
            typeof event.runId === 'string' &&
            event.runId !== prev.runId,
    )
}

/** A real node finish is TOOL_CALL_RESULT. Terminal cleanup also emits
 * TOOL_CALL_END for whatever is still open, with no result — that must not
 * look like the primary pipeline completed. */
function toolResultIsError(content: unknown): boolean {
    if (typeof content !== 'string') {
        return false
    }
    try {
        const parsed = JSON.parse(content) as { isError?: unknown }
        return parsed.isError === true
    } catch {
        return false
    }
}

function reduceToolResult(prev: SignalState, event: WireEvent): SignalState {
    if (!prev.runActive || isOtherRun(prev, event)) {
        return prev
    }
    if (
        typeof event.toolCallId !== 'string' ||
        toolResultIsError(event.content)
    ) {
        return prev
    }
    const stageLabel = CONTENT_STAGE_LABELS[event.toolCallId]
    if (stageLabel) {
        return {
            ...prev,
            contentReady:
                prev.contentReady ||
                event.toolCallId === AI_CREATE_STREAM_NODE.GENERATE_LLM_CONTENT,
            contentStageCount: prev.contentStageCount + 1,
            contentStageLabel: stageLabel,
        }
    }
    if (event.toolCallId === AI_CREATE_STREAM_NODE.REVISE_STOREFRONTS) {
        return { ...prev, primaryPipelineFinished: true }
    }
    return prev
}

export function reduceSignals(prev: SignalState, raw: unknown): SignalState {
    const event = raw as WireEvent
    if (typeof event?.type !== 'string') {
        return prev
    }
    switch (event.type) {
        case 'RUN_STARTED':
            return {
                ...prev,
                runId:
                    typeof event.runId === 'string' ? event.runId : prev.runId,
                runActive: true,
                hasActivity: true,
                runFailed: false,
                runErrorMessage: null,
                primaryPipelineFinished: false,
            }
        case 'RUN_FINISHED':
        case 'RUN_ERROR': {
            // A superseded run's terminal must not close or fail the current one.
            if (isOtherRun(prev, event)) {
                return prev
            }
            if (event.type === 'RUN_ERROR') {
                return {
                    ...prev,
                    runActive: false,
                    runFailed: true,
                    runErrorMessage:
                        typeof event.message === 'string'
                            ? event.message
                            : 'The build failed',
                }
            }
            return { ...prev, runActive: false }
        }
        case 'TOOL_CALL_RESULT':
            return reduceToolResult(prev, event)
        case 'CUSTOM':
            if (
                event.name === AI_CREATE_AG_UI_CUSTOM_EVENT.NOTE &&
                typeof event.value?.node === 'string' &&
                EDIT_NOTE_NODES.has(event.value.node)
            ) {
                return { ...prev, editNoteCount: prev.editNoteCount + 1 }
            }
            return prev
        default:
            return prev
    }
}

export type StreamGate = 'runFailed' | 'primaryFinished'

/** Rising edges only, so a replayed signal does not redirect or fail twice. */
export function streamGateTransitions(
    prev: AiCreateRunSignals,
    next: AiCreateRunSignals,
): StreamGate[] {
    const gates: StreamGate[] = []
    if (next.runFailed && !prev.runFailed) {
        gates.push('runFailed')
    }
    if (next.primaryPipelineFinished && !prev.primaryPipelineFinished) {
        gates.push('primaryFinished')
    }
    return gates
}

const AiCreateRunSignalsContext =
    createContext<AiCreateRunSignals>(EMPTY_SIGNALS)

export function AiCreateRunSignalsProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const agent = useAiCreateAgent()
    const [signals, setSignals] = useState<
        AiCreateRunSignals & { runId?: string }
    >(EMPTY_SIGNALS)

    // The eventsource polyfill reconnects internally without surfacing an
    // error, so the run-log replay arrives with no stream-reset signal. Dedupe
    // by event bytes (replays are byte-identical) instead of trusting resets.
    // The window resets only when a genuinely new run starts — the server
    // replays only the active run's log, so the old run can never resurface
    // after that — which also bounds the set to one run's events. Clearing at
    // terminals instead would reopen the window to late replays.
    const seenRef = useRef<Set<string>>(new Set())
    const seenRunRef = useRef<string | null>(null)

    useEffect(() => {
        const unsubscribeEvents = agent.subscribeSessionEvents((event) => {
            const key = JSON.stringify(event)
            if (seenRef.current.has(key)) {
                return
            }
            const { type, runId } = (event ?? {}) as {
                type?: unknown
                runId?: unknown
            }
            if (
                type === 'RUN_STARTED' &&
                typeof runId === 'string' &&
                runId !== seenRunRef.current
            ) {
                seenRef.current = new Set()
                seenRunRef.current = runId
            }
            seenRef.current.add(key)
            setSignals((prev) => reduceSignals(prev, event))
        })
        const unsubscribeReset = agent.subscribeStreamReset(() => {
            seenRef.current = new Set()
            seenRunRef.current = null
            setSignals(EMPTY_SIGNALS)
        })
        return () => {
            unsubscribeEvents()
            unsubscribeReset()
        }
    }, [agent])

    return (
        <AiCreateRunSignalsContext.Provider value={signals}>
            {children}
        </AiCreateRunSignalsContext.Provider>
    )
}

export function useAiCreateRunSignals(): AiCreateRunSignals {
    return useContext(AiCreateRunSignalsContext)
}
